import React, { useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polygon } from 'react-leaflet';
import type { LatLngExpression } from 'leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './Map.css';
import logo from '../../assets/logo_image.png';
import brazilGeo from './brazil.json';

const markerIcon = new L.DivIcon({
  html: `
    <div class="custom-pin">
      <img src="${logo}" class="custom-pin-logo" alt="InovaFert" />
    </div>
  `,
  className: '',
  iconSize: [36, 48],
  iconAnchor: [18, 48],
  popupAnchor: [0, -50],
});

const CENTER: LatLngExpression = [-15.0, -53.0];

const clients = [
  { name: "Hologene", city: "Gaspar/SC", training: "Parceiro", year: "2024", instagram: "", lat: -26.9307306, lng: -48.9566824 },
  { name: "Fleming", city: "Jaraguá do Sul/SC", training: "Presencial completo", year: "2025", instagram: "flemingoficial", lat: -26.4897432, lng: -49.0778063 },
  { name: "LabVital", city: "Santo Amaro da Imperatriz/SC", training: "Presencial completo", year: "2025", instagram: "labvital", lat: -27.6865324, lng: -48.7769405 },
  { name: "Biomax", city: "Maximiliano de Almeida/RS", training: "Presencial básico", year: "2025", instagram: "biomaxlaboratorio", lat: -27.6326512, lng: -51.8046768 },
  { name: "Santa Izabel", city: "Três Rios/RJ", training: "Presencial", year: "2025", instagram: "labsantaizabel", lat: -22.1167884, lng: -43.2087226 },
  { name: "Laborclínico", city: "Rio do Sul/SC", training: "Presencial completo", year: "2025", instagram: "laborclinicolaboratorio", lat: -27.2162607, lng: -49.643654 },
  { name: "Santa Clara", city: "Araçuai/MG", training: "Online Kruger", year: "2025", instagram: "laboratoriosantaclara", lat: -16.8484451, lng: -42.0661868 },
  { name: "São Rafael", city: "Iturama/MG", training: "Online Aprofundando", year: "2025", instagram: "laboratoriosaorafael1", lat: -19.7272733, lng: -50.193488 },
  { name: "Ximenes", city: "Santo Antônio da Platina/PR", training: "Presencial básico", year: "2025", instagram: "laboratorioximenes", lat: -23.2957157, lng: -50.0778957 },
  { name: "Bioanálise", city: "Loanda/PR", training: "Online Kruger", year: "2025", instagram: "bioanaliseloanda", lat: -22.9320944, lng: -53.1352452 },
  { name: "Roos", city: "Venâncio Aires/RS", training: "Online", year: "2025", instagram: "rooslaboratorio", lat: -29.614306, lng: -52.193159 },
  { name: "Exame", city: "Barra do Garças/MT", training: "Online Aprofundando e Kruger", year: "2025", instagram: "laboratorioexamebg", lat: -15.8915907, lng: -52.2618392 },
  { name: "Schell", city: "Seara/SC", training: "Online Aprofundando e Kruger", year: "2025", instagram: "laboratorioschell", lat: -27.1528578, lng: -52.3096985 },
  { name: "Hoffmann", city: "Brusque/SC", training: "Presencial", year: "2025", instagram: "laboratoriohoffmann", lat: -27.0964628, lng: -48.9136323 },
  { name: "Vida", city: "Itambacuri/MG", training: "Online Kruger", year: "2025", instagram: "vida_laboratorio", lat: -18.0391974, lng: -41.6751876 },
  { name: "Santé", city: "Pontes e Lacerda/MT", training: "Online Aprofundando e Kruger", year: "2025", instagram: "laboratorio_sante", lat: -15.2315171, lng: -59.3349512 },
  { name: "Dornelas", city: "Guanhães/MG", training: "Online Kruger", year: "2025", instagram: "laboratoriodornelas", lat: -18.7753627, lng: -42.9340634 },
  { name: "Portão", city: "Portão/RS", training: "Online Aprofundando", year: "2025", instagram: "laboratorioportao", lat: -29.6932554, lng: -51.2331837 },
  { name: "Bhio3", city: "Itajubá/MG", training: "Online Aprofundando e Kruger", year: "2026", instagram: "bhio3", lat: -22.4238234, lng: -45.4524156 },
  { name: "Haima", city: "Ampére/PR", training: "Online Aprofundando e Kruger", year: "2026", instagram: "laboratoriohaima", lat: -25.9168712, lng: -53.4742414 },
  { name: "Microlab", city: "Patos/PB", training: "Online Aprofundando e Kruger", year: "2026", instagram: "microlabpb", lat: -7.0258285, lng: -37.2766817 },
  { name: "Santa Clara", city: "Ouro Preto do Oeste/RO", training: "Online Fragmentação de DNA", year: "2026", instagram: "staclaralab", lat: -10.717914, lng: -62.2566611 },
  { name: "São Lucas", city: "Nova Candelária/RS", training: "Presencial", year: "2026", instagram: "saolucaslaboratorios", lat: -27.6110759, lng: -54.1065402 },
];

const worldBounds: LatLngExpression[] = [
  [-90, -180], [-90, 180], [90, 180], [90, -180],
];

const Map: React.FC = () => {
  const brazilCoords: LatLngExpression[] = useMemo(() => {
    return (brazilGeo as any).coordinates[0].map(([lng, lat]: [number, number]) => [lat, lng] as LatLngExpression);
  }, []);

  const maskPositions: LatLngExpression[][] = [worldBounds, brazilCoords];

  return (
    <div id="map" className="map-section">
      <div className="map-container">
      <MapContainer
        center={CENTER}
        zoom={4.8}
        zoomSnap={0.8} 
        attributionControl={false}
        scrollWheelZoom={false}
        style={{ width: '100%', height: '100%' }}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <Polygon
          positions={maskPositions}
          pathOptions={{ color: 'none', fillColor: '#000', fillOpacity: 0.4 }}
        />
        {clients.map((client, index) => (
          <Marker key={index} position={[client.lat, client.lng]} icon={markerIcon}>
            <Popup>
              <div className="popup-content">
                <div className="popup-header">{client.name}</div>
                <div className="popup-divider" />
                <div className="popup-row">
                  <span className="popup-icon">📍</span>
                  <span>{client.city}</span>
                </div>
                <div className="popup-row">
                  <span className="popup-icon">🎓</span>
                  <span><span className="popup-label">Treinamento:</span> {client.training}</span>
                </div>
                <div className="popup-row">
                  <span className="popup-icon">📅</span>
                  <span><span className="popup-label">Ano:</span> {client.year}</span>
                </div>
                {client.instagram && (
                  <div className="popup-row">
                    <span className="popup-icon">📸</span>
                    <span><span className="popup-label">Instagram:</span> <a href={`https://www.instagram.com/${client.instagram}`} target="_blank" rel="noopener noreferrer" className="popup-link">@{client.instagram}</a></span>
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      </div>
    </div>
  );
};

export default Map;
